import { type FC, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { AdminDashboardExpenseCategoryRow } from "@/admin/entities/adminDashboard";
import {
  buildExpenseCategoryRows,
  expenseCategoryTotals,
  expensesHref,
  formatCount,
  formatPercent,
  formatUsd,
  type DayRange,
} from "../model";
import { QueryErrorAlert } from "./QueryErrorAlert";

interface ExpensesByCategoryTableProps {
  rows: AdminDashboardExpenseCategoryRow[];
  range: DayRange;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

/** «Траты за период» по статьям (спека §3.6). */
export const ExpensesByCategoryTable: FC<ExpensesByCategoryTableProps> = ({
  rows,
  range,
  loading,
  error,
  onRetry,
}) => {
  const tableRows = useMemo(() => buildExpenseCategoryRows(rows), [rows]);
  const totals = useMemo(() => expenseCategoryTotals(rows), [rows]);

  let body;
  if (error) {
    body = (
      <CardContent>
        <QueryErrorAlert onRetry={onRetry} />
      </CardContent>
    );
  } else if (loading) {
    body = (
      <CardContent>
        <Skeleton variant="rounded" height={160} />
      </CardContent>
    );
  } else if (rows.length === 0) {
    body = (
      <CardContent>
        <Typography color="text.secondary">Трат за период нет</Typography>
      </CardContent>
    );
  } else {
    body = (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Статья</TableCell>
              <TableCell align="right">Сумма</TableCell>
              <TableCell>Доля</TableCell>
              <TableCell align="right">Записей</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.map((row) => (
              <TableRow key={row.category} hover>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right">{formatUsd(row.amountUSD)}</TableCell>
                <TableCell sx={{ minWidth: 140 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center" }}
                  >
                    <LinearProgress
                      variant="determinate"
                      color="error"
                      value={Math.max(0, Math.min(100, row.share ?? 0))}
                      sx={{ flexGrow: 1 }}
                    />
                    <Typography variant="body2" sx={{ minWidth: 48 }}>
                      {row.share === null ? "" : formatPercent(row.share)}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell align="right">{formatCount(row.count)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell>Итого</TableCell>
              <TableCell align="right">{formatUsd(totals.amountUSD)}</TableCell>
              <TableCell />
              <TableCell align="right">{formatCount(totals.count)}</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Card sx={{ height: "100%" }}>
      <CardHeader
        title="Траты за период"
        subheader="По статьям, USD"
        action={
          <Button component={RouterLink} to={expensesHref(range)} size="small">
            Все траты
          </Button>
        }
      />
      {body}
    </Card>
  );
};
